import { cartQuery } from '@/api/carts/cartQueries';
import type { CartPriceBreakdown } from '@/domain/Cart';
import { CartPeriod } from '@/domain/Cart';
import { cn } from '@/lib/cn';
import { useBreakpoint } from '@/lib/contexts/BreakpointContext';
import { useQuery } from '@tanstack/react-query';

interface CartSummaryItemProps {
  label: string;
  discount?: number;
  previous?: string;
  value: string;
}
interface CartSummaryProps {
  cartId: number;
  promoPriceBreakdown?: CartPriceBreakdown;
}

const CartSummaryItem = ({
  label,
  discount,
  previous,
  value,
}: CartSummaryItemProps) => {
  return (
    <div className="flex flex-col justify-between md:h-7.5 md:flex-row">
      <p className="font-medium">
        {label}
        <span className="md:hidden">:</span>
      </p>

      <div
        className={cn(
          'grid h-7.5 w-70 grid-cols-[1fr_1fr_6.625rem] items-center gap-2 md:grid-cols-[6.625rem_1fr_1fr]',
          !discount && !previous && 'flex justify-between',
        )}
      >
        {discount ? (
          <p className="bg-primary text-primary-foreground order-last flex h-7.5 w-fit items-center justify-center rounded-full px-3 text-center text-xs md:order-0">{`Save ${discount} %`}</p>
        ) : (
          <p className="order-last md:order-0" />
        )}

        <p className="text-muted-foreground line-through">{previous ?? ''}</p>
        <p className="md:text-right">{value}</p>
      </div>
    </div>
  );
};

export const CartSummary = ({
  cartId,
  promoPriceBreakdown,
}: CartSummaryProps) => {
  const { isTablet } = useBreakpoint();
  const { data: cart } = useQuery(cartQuery(cartId));

  const period = cart?.data.period ?? 1;

  const total = cart?.data.price_breakdown?.total ?? 0;
  const subtotal = cart?.data.price_breakdown?.subtotal ?? 0;

  const subMonthlyPrice = subtotal / period;
  const monthlyPrice = total / period;

  const promoDiscountPercent = promoPriceBreakdown?.promo_discount_percent ?? 0;
  const totalWithPromo = promoPriceBreakdown?.total ?? 0;

  const hasItems = (cart?.data.price_breakdown?.total ?? 0) > 0;

  return (
    <div className="flex flex-col gap-2.5">
      <CartSummaryItem
        label="Monthly cost"
        discount={
          hasItems && period !== CartPeriod.MONTHLY
            ? cart?.data.price_breakdown?.period_discount_percent
            : undefined
        }
        previous={
          hasItems && period !== CartPeriod.MONTHLY
            ? `€${(subMonthlyPrice / 100).toFixed(2)}`
            : undefined
        }
        value={`€${(monthlyPrice / 100).toFixed(2)}`}
      />

      <CartSummaryItem
        label="Subscription cost"
        previous={
          hasItems && period !== CartPeriod.MONTHLY
            ? `€${(subtotal / 100).toFixed(2)}`
            : undefined
        }
        value={`€${(total / 100).toFixed(2)}`}
      />

      {hasItems && promoPriceBreakdown && (
        <CartSummaryItem
          label="Subscription cost with additional discount"
          discount={promoDiscountPercent || undefined}
          previous={`€${(total / 100).toFixed(2)}`}
          value={`€${(totalWithPromo / 100).toFixed(2)}`}
        />
      )}

      {isTablet ? (
        <CartSummaryItem
          label="Subscription period"
          value={`${period.toString()} month(s)`}
        />
      ) : (
        <p className="text-muted-foreground font-medium">
          Subscription period{' '}
          <span className="text-foreground font-normal">
            - {period.toString()} month(s)
          </span>
        </p>
      )}
    </div>
  );
};
